import React, { useEffect, useReducer, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';

import { Box } from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import useFeatureFlag from '../../../../hooks/useFeatureFlag';
import useAsyncEffect from '../../../../../../packages/hooks/src/useAsyncEffect';
import { useJanitor } from '../../../../hooks/useJanitor';

import { ENV, SITE_ROOT } from '../../../../config';

import analytics from '../../../../modules/analytics';
import { openExternalUrl } from '../../../../modules/openExternalUrl';
import { toSlugCase } from '../../../../modules/stringUtils';

import withStore from '../../../withStore';

import InfoButtonContainer from './info';
import {
  getNumberOfSearchAppearances, getSelfPromotionDates, extendSearchBoostEligibleDate,
} from '../../../../modules/Api';
import {
  Active, Earning, Extending, PreEarning,
} from './modes';

import {
  PRO_SEARCH_BOOST_FLAG,
  PRO_SEARCH_BOOST_DAYS_BOOSTED,
  PRO_SEARCH_BOOST_DAYS_BOOST_ELEGIBLE,
  PRO_SEARCH_BOOST_LIVE_SYNC_DELAY,
  PRO_SEARCH_BOOST_MODES,
  PRO_SEARCH_BOOST_CTAS,
} from '../../constants';

export const UNIT_OF_TIME = ENV === 'production' ? 'days' : 'minutes';

/**
 * Function to determine if pro has promoted their profile within the last three days
 * @param {string} lastSelfPromotedDate - the datetime of when the profile was last promoted
 * @return {boolean}
 */
export const hasPromotedProfileWithinLast3Days = lastSelfPromotedDate => {
  if (lastSelfPromotedDate) {
    return moment.utc(lastSelfPromotedDate) >= moment.utc().subtract('days', 3);
  }
  return false;
};

const scrollIntoViewSources = new Set([
  'search_boost_eligibility',
  'search_boost_eligibility_reminder',
  'search_boosted',
]);

const stateReducer = (state, action) => {
  const { payload, type } = action;

  switch (type) {
    case 'setDaysLeft':
      return { ...state, daysLeft: payload };

    case 'setAddPreview':
      return { ...state, addPreview: payload };

    case 'setCtaData':
      return { ...state, ctaData: payload };

    case 'setAppearances':
      return { ...state, appearances: payload };

    case 'setBookings':
      return { ...state, currentValue: payload };

    case 'setMode':
      return { ...state, mode: payload };

    case 'setShouldShow':
      return { ...state, shouldShow: payload };

    case 'setShowExtendBoost':
      return { ...state, showExtendBoost: payload };

    case 'setExtended':
      return { ...state, extended: payload };

    default:
      return { ...state };
  }
};

const SearchBoostContainer = ({
  linkTo,
  provider,
  source,
  utmSource,
}) => {
  const { enabled: searchBoostFlagEnabled } = useFeatureFlag(PRO_SEARCH_BOOST_FLAG);
  const [state, dispatch] = useReducer(stateReducer, {});
  const containerRef = useRef();
  const janitor = useJanitor();

  const trackEvent = event => {
    const baseParams = {
      provider_id: provider.id,
      days_left_in_boost: state.daysLeft,
      source,
      bookings: state.currentValue,
      cta: state.cta && state.cta.text,
      extended: state.extended,
    };

    analytics.track(`pro_home_search_boost_${event}`, {
      ...baseParams,
      mode: state.mode,
    });
  };

  const handlePreview = () => {
    trackEvent('preview_clicked');

    // Specifically open the search in a new browser window so it's easy to return from preview.
    openExternalUrl(`${SITE_ROOT}/m/search//${toSlugCase(provider.name)}`);
  };

  const parseCTA = data => {
    if (!data) {
      return null;
    }

    const { text, tracking, route } = data;

    return {
      text,
      onClick: () => {
        trackEvent(tracking);

        linkTo(route, { providerId: provider.id });
      },
    };
  };

  /**
   * Function to calcuate dates to determine what will be rendered for the search boost section
   * @param {string} statusDate - can be either start date to earn search boost
   *                              or search boost eligible date (date at which search boost began)
   * @param {string} earningEndDate - end date to earn search boost
   * @param {boolean} isBoosted - whether the pro is already boosted
   * @param {boolean} boostExtended - pro has extended time to earn search boost
   * @return {object} show: to show the search boost section (eligible to receive boost or boosted)
   *                  left: days left for either eligible to recieve boost, or boosted
   *                  preview: show the preview button
   *                  showBoostExtension: show the boost extension component to extend search boost
   */
  const getDates = (statusDate, earningEndDate, isBoosted, boostExtended) => {
    if (!statusDate) {
      return {
        show: true,
      };
    }

    let days = moment.duration(moment.utc()
      .diff(moment.utc(statusDate)));

    days = ENV === 'production' ? days.asDays() : days.asMinutes();

    const maxDaysToEarnSearchBoost = boostExtended
      ? moment.utc(earningEndDate).diff(moment.utc(statusDate), UNIT_OF_TIME)
      : PRO_SEARCH_BOOST_DAYS_BOOST_ELEGIBLE;

    const maxDays = !isBoosted
      ? maxDaysToEarnSearchBoost
      : PRO_SEARCH_BOOST_DAYS_BOOSTED;

    const left = Math.ceil(maxDays - days);

    const withinSevenDayExtensionWindow = (
      // current time is after the earning end date
      moment.utc().isAfter(moment.utc(earningEndDate))
      // current time is still before 7 days after earning end date
      && moment.utc().isBefore(moment.utc(earningEndDate).add(7, UNIT_OF_TIME))
    );

    const showBoostExtension = (
      withinSevenDayExtensionWindow
      && !isBoosted
      && !boostExtended
    );

    const preview = (
      statusDate
      && (moment.utc() > moment.utc(statusDate).add(PRO_SEARCH_BOOST_LIVE_SYNC_DELAY, 'minutes'))
    );

    return {
      show: (days < maxDays) || showBoostExtension,
      left,
      preview,
      showBoostExtension,
    };
  };

  /**
   * handles button click on the extendSearchBoost component
   */
  const handleExtendButtonClicked = () => {
    janitor.addPromise(extendSearchBoostEligibleDate(provider.id))
      .then(res => {
        const {
          profile_search_boost_earning_start_date: profileSearchBoostEarningStartDate,
          profile_search_boost_earning_end_date: profileSearchBoostEarningEndDate,
          profile_search_boost_eligible_date: profileSearchBoostEligibleDate,
          num_appointments: boostAppointmentNumber,
        } = res;
        const isBoosted = profileSearchBoostEligibleDate;
        const { left } = getDates(
          profileSearchBoostEligibleDate || profileSearchBoostEarningStartDate,
          profileSearchBoostEarningEndDate,
          isBoosted,
          true,
        );
        dispatch({ type: 'setExtended', payload: true });
        dispatch({ type: 'setDaysLeft', payload: left });
        if (isBoosted) {
          dispatch({ type: 'setBookings', payload: boostAppointmentNumber });
          dispatch({ type: 'setMode', payload: PRO_SEARCH_BOOST_MODES.active });
        } else {
          dispatch({ type: 'setMode', payload: PRO_SEARCH_BOOST_MODES.earning });
        }
      });
  };

  const getBoostModeComponent = ({
    mode,
    ctaData,
    addPreview,
    ...restProps
  }) => {
    const cta = parseCTA(ctaData);
    const preview = addPreview ? handlePreview : null;
    switch (mode) {
      case PRO_SEARCH_BOOST_MODES.active:
        return <Active {...restProps} cta={cta} preview={preview} />;
      case PRO_SEARCH_BOOST_MODES.earning:
        return <Earning {...restProps} cta={cta} />;
      case PRO_SEARCH_BOOST_MODES.preEarning:
        return <PreEarning {...restProps} />;
      case PRO_SEARCH_BOOST_MODES.extending:
        return (
          <Extending
            {...restProps}
            deadline={moment().add(7, 'days')}
            onExtendButtonClicked={handleExtendButtonClicked}
          />
        );

      default:
        throw new Error('boost mode not supported');
    }
  };

  // after determining enabled, calculate statusDate and boosted
  useAsyncEffect(async () => {
    if (searchBoostFlagEnabled !== undefined) {
      const {
        profile_phase_one_complete_date: profileCompleteDate,
        profile_search_boost_earning_start_date: earningStartDate,
        profile_search_boost_earning_end_date: earningEndDate,
        profile_search_boost_eligible_date: elegibleDate, // boosted starting on this date
        profile_search_boost_extension_creation_time: profileSearchBoostExtensionCreationTime,
        profile_search_boost_num_appts: boostAppointmentNumber, // appt count to reach threshold
      } = provider;

      const isBoosted = (searchBoostFlagEnabled && !!elegibleDate);
      const boostExtended = !!profileSearchBoostExtensionCreationTime;
      dispatch({ type: 'setExtended', payload: boostExtended });

      const {
        show,
        left,
        preview,
        showBoostExtension,
      } = getDates(elegibleDate || earningStartDate, earningEndDate, isBoosted, boostExtended);

      if (!show) {
        return;
      }

      if (!profileCompleteDate) {
        dispatch({ type: 'setMode', payload: PRO_SEARCH_BOOST_MODES.preEarning });
        dispatch({ type: 'setShouldShow', payload: true });
        return;
      }

      if (isBoosted) {
        const { count } = await getNumberOfSearchAppearances(provider.id);

        dispatch({ type: 'setAppearances', payload: count });
        dispatch({ type: 'setMode', payload: PRO_SEARCH_BOOST_MODES.active });
      } else {
        dispatch({ type: 'setMode', payload: PRO_SEARCH_BOOST_MODES.earning });
      }

      dispatch({ type: 'setBookings', payload: boostAppointmentNumber });

      const selfPromotedDates = await getSelfPromotionDates(provider.id);
      const { last_self_promoted_date: lastSelfPromotedDate } = selfPromotedDates;
      const promotedWithinLast3Days = hasPromotedProfileWithinLast3Days(lastSelfPromotedDate);
      const ctaData = PRO_SEARCH_BOOST_CTAS[promotedWithinLast3Days ? 'addAppointments' : 'shareProfile'];
      dispatch({ type: 'setCtaData', payload: ctaData });

      dispatch({ type: 'setDaysLeft', payload: left });

      dispatch({ type: 'setAddPreview', payload: preview });

      if (showBoostExtension) {
        dispatch({ type: 'setMode', payload: PRO_SEARCH_BOOST_MODES.extending });
      }

      dispatch({ type: 'setShouldShow', payload: true });
    }
  }, [searchBoostFlagEnabled]);

  useEffect(() => {
    if (state.shouldShow) {
      trackEvent('viewed');
    }
  }, [state]);

  // If we're inbound from a search-boost related link, scroll the search boost
  // component into view.
  useEffect(() => {
    if (
      scrollIntoViewSources.has(utmSource)
      && state.shouldShow
      && state.mode
    ) {
      setTimeout(() => containerRef.current && containerRef.current.scrollIntoView(), 100);
    }
  }, [utmSource, containerRef.current, state.shouldShow, state.mode]);

  return state.shouldShow && state.mode ? (
    <Box
      color={colors.whiteWash}
      borderColor={colors.lightGrey}
      borderWidth={0.5}
      paddingBottom={state.mode === PRO_SEARCH_BOOST_MODES.active ? 24 : 0}
      style={{
        marginTop: 4,
        marginBottom: 4,
      }}
    >
      {typeof document !== 'undefined' && <span id="search-boost" ref={containerRef} />}
      <Box
        paddingX={16}
        paddingTop={12}
      >
        {getBoostModeComponent(state)}
      </Box>
      {state.mode !== PRO_SEARCH_BOOST_MODES.active && (
        <InfoButtonContainer {...state} providerId={provider.id} />
      )}
    </Box>
  ) : null;
};

SearchBoostContainer.propTypes = {
  /** function to providing routing */
  linkTo: PropTypes.func.isRequired,
  /** provider object */
  provider: PropTypes.object.isRequired,
  /** for tracking to indicate how event was triggered */
  source: PropTypes.string,
  utmSource: PropTypes.string,
};

SearchBoostContainer.defaultProps = {
  source: '',
  utmSource: '',
};

const mapStateToProps = state => ({
  source: state.route.params.notificationSource,
  utmSource: state.route.params.utm_source,
});

const mapDispatchToProps = dispatch => ({
  linkTo: (route, params, notify = true) => {
    dispatch.route.go({
      route,
      params,
      notify,
    });
  },
});

export default withStore(connect(mapStateToProps, mapDispatchToProps)(SearchBoostContainer));
